import React, { useMemo, useEffect, useCallback } from 'react';
import { makeStyles, Container, Grid, Paper } from '@material-ui/core';
import { injectIntl } from 'react-intl';
import Breadcrumb from 'app/components/BreadCrumb';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import notification from 'app/config/notification';
import { clearFiles, clearCity } from 'app/store/slices/forms';
import { clearServiceDetails, clearServiceDocuments } from 'app/store/slices/services';
import { showLoading, hideLoading, setFromLogin } from 'app/store/slices/settings';
import { clearNewRequest, clearRequestDetails, fetchSaveRequest } from 'app/store/slices/requests';
import { preparePayload } from 'app/utils/forms/functions';
import { FormServiceContext } from 'app/utils/contexts';
import ChangeEmailRegistration from 'app/components/ServicesForms/Registration/ChangeEmail';
import requestEndpoints from 'app/api/services/requestEndpoints';
import alertsEndpoint from 'app/api/services/alertsEndpoint';
import { errorHandler } from 'app/utils/errorHandler';
import { URLS } from 'app/config/urls';
import { STATUS_CODES } from 'app/config/status';
import { setupChangeEmailPayload } from 'app/utils/forms/services/change-email';
import useChangeEmail from './hooks/useChangeEmail';
import styles from './styles';
const useStyles = makeStyles(styles);

function ChangeEmail(props) {
    const classes = useStyles();
    const history = useHistory();
    const dispatch = useDispatch();

    const { intl } = props;

    const service = useSelector((state) => state.services.details);
    const request = useSelector((state) => state.requests.details);
    const documentsStore = useSelector((state) => state.services.documents);
    const registrationUserInfo = useSelector((state) => state.auth.registrationInfo);
    const activeCompany = useSelector((state) => state.company.activeCompany);
    const userAdmin = useSelector((state) => state.auth.userAdmin);

    if (Object.keys(service).length === 0) history.push(generatePath(URLS.SERVICES));

    const { initialValues, editing, validationSchema, canEdit, loadingDocuments, setLoadingDocuments } = useChangeEmail(
        { cdServico: service.cdServico },
    );

    useEffect(() => {
        return () => {
            dispatch(clearCity());
            dispatch(clearFiles());
            dispatch(clearServiceDetails());
            dispatch(clearServiceDocuments());
            dispatch(clearNewRequest());
            dispatch(clearRequestDetails());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetAndGo = useCallback(() => {
        dispatch(hideLoading());
        dispatch(setFromLogin(false));
        history.push(URLS.LOGIN);
    }, [dispatch, history]);

    const onSave = useCallback(
        async (payload) => {
            if (!canEdit) return;
            dispatch(showLoading());
            return await fetchSaveRequest({
                isFormData: true,
                preparedPayload: payload,
                dispatch,
                intl,
                showMessage: true,
                cdService: service.cdServico,
            });
        },
        [canEdit, dispatch, intl, service.cdServico],
    );

    const onSubmit = useCallback(
        async (values) => {
            if (!canEdit) return;

            let valuesCopy = JSON.parse(JSON.stringify(values));
            const currentStatusCode = request.cdSituacao;

            let dataToPayload = {
                cdServico: service.cdServico,
            };

            if (request && request.cdServico) {
                dataToPayload = { ...request };
                if (request.cdSolicitacao) valuesCopy.cdSolicitacao = request.cdSolicitacao;
                if (request.situacao) valuesCopy.situacao = request.situacao;
            }

            const documentsObject = {
                store: documentsStore,
                files: values.documentos,
            };

            valuesCopy.cdServico = service.cdServico;
            valuesCopy = setupChangeEmailPayload({ values: valuesCopy, intl });
            if (!valuesCopy) return;

            const preparedPayload = preparePayload({
                values: valuesCopy,
                documents: documentsObject,
                params: dataToPayload,
                activeCompany,
                userAdmin,
                registrationUserInfo,
                cdServico: service.cdServico,
            });

            if (preparedPayload.errors) {
                notification(intl.formatMessage({ id: 'MESSAGES.SERVICE.FORMS.MISSING_MANDATORY_INFOS' }), {
                    toastId: 'MISSING_MANDATORY_INFOS',
                    type: 'warning',
                });
                return false;
            }

            try {
                const responseSave = await onSave(preparedPayload, true);
                if (responseSave && responseSave.status === 200) {
                    if (request?.cdSolicitacao) {
                        await requestEndpoints.finishRequest(request?.cdSolicitacao);
                        if (currentStatusCode === STATUS_CODES.AGUARDANDO_RESOLUCAO_PENDENCIAS) {
                            await alertsEndpoint.deleteAlert({ cdSolicitacao: request.cdSolicitacao });
                        }
                    }
                    resetAndGo();
                }
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
            dispatch(hideLoading());
        },
        [
            activeCompany,
            canEdit,
            dispatch,
            documentsStore,
            intl,
            onSave,
            registrationUserInfo,
            request,
            resetAndGo,
            service.cdServico,
            userAdmin,
        ],
    );

    const validateForm = (functionValidation) => {
        functionValidation.then((res) => {
            if (Object.keys(res).length > 0) {
                notification('O(s) campo(s) sinalizado(s) precisam de revisão.', {
                    toastId: 'VALIDATION',
                    type: 'error',
                });
            }
        });
    };

    const contextProvider = useMemo(() => {
        return {
            loadingDocuments,
            setLoadingDocuments,
            validateForm,
            onSubmit,
            onSave,
            onCancel: () => {},
            initialValues,
            validationSchema: validationSchema,
            hideActionBtn: true,
            editing,
            canEdit,
        };
    }, [canEdit, editing, initialValues, loadingDocuments, onSave, onSubmit, validationSchema, setLoadingDocuments]);

    const breadCrumb = [
        {
            title: intl.formatMessage({ id: 'LABELS.COMMONS.CHANGE_EMAIL' }),
            path: '',
        },
    ];

    const rightInfo = request?.nuSolicitacaoFmt
        ? `${intl.formatMessage({ id: 'LABELS.SERVICES.REQUEST_NUMBER' })} ${request.nuSolicitacaoFmt}`
        : '';

    const handleOnBack = () => {
        const isGuest = Object.keys(registrationUserInfo).length === 0;
        if (isGuest || (request.nuSolicitacaoFmt && request.nuSolicitacaoFmt !== '')) {
            dispatch(setFromLogin(false));
            history.push(URLS.START);
        } else {
            history.push(generatePath(URLS.SERVICES));
        }
    };

    return (
        <Container>
            <Breadcrumb onBack={() => handleOnBack()} breadCrumb={breadCrumb} rightInfo={rightInfo} />
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper className={classes.container} elevation={3}>
                        <FormServiceContext.Provider value={contextProvider}>
                            <ChangeEmailRegistration {...props} />
                        </FormServiceContext.Provider>
                    </Paper>
                </Grid>
            </Grid>
        </Container>
    );
}

export default injectIntl(ChangeEmail);
